import { useEffect } from 'react';
import { Link } from 'react-router-dom';
import Navbar from '@/components/feature/Navbar';
import Footer from '@/components/feature/Footer';

interface ArticleSection {
  title: string;
  content: React.ReactNode;
}

interface FAQ {
  q: string;
  a: string;
}

interface ArticleLayoutProps {
  tag: string;
  tagClass?: string;
  title: string;
  description: string;
  heroImage: string;
  heroAlt: string;
  author: string;
  date: string;
  readingTime: string;
  sections: ArticleSection[];
  faqs?: FAQ[];
  conclusion?: React.ReactNode;
}

export default function ArticleLayout({
  tag,
  tagClass = 'bg-primary-100 text-primary-700',
  title,
  description,
  heroImage,
  heroAlt,
  author,
  date,
  readingTime,
  sections,
  faqs,
  conclusion,
}: ArticleLayoutProps) {
  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);

  return (
    <>
      <Navbar />
      <main>
        <article className="pt-24 pb-16 md:pb-24">
          <div className="max-w-3xl mx-auto px-6 md:px-10">
            <nav className="flex items-center gap-2 text-sm text-foreground-400 mb-8">
              <Link to="/" className="hover:text-primary-600 transition-colors cursor-pointer">Home</Link>
              <i className="ri-arrow-right-s-line text-xs w-4 h-4 flex items-center justify-center"></i>
              <span className="text-foreground-600">{tag}</span>
            </nav>

            <header className="mb-12">
              <span className={`inline-block px-3 py-1 rounded-full text-xs font-semibold tracking-wide uppercase mb-4 ${tagClass}`}>
                {tag}
              </span>
              <h1 className="font-heading text-3xl md:text-4xl lg:text-5xl font-bold text-foreground-900 leading-tight mb-4">
                {title}
              </h1>
              <p className="text-foreground-500 text-base leading-relaxed">
                {description}
              </p>
              <div className="flex items-center gap-4 mt-6 pt-6 border-t border-background-200/70">
                <div className="w-10 h-10 rounded-full bg-accent-100 flex items-center justify-center">
                  <span className="text-accent-700 font-semibold text-sm">IS</span>
                </div>
                <div>
                  <div className="text-foreground-900 font-medium text-sm">{author}</div>
                  <div className="text-foreground-400 text-xs">{date} &middot; {readingTime}</div>
                </div>
              </div>
            </header>

            <div className="rounded-2xl overflow-hidden mb-12">
              <img
                src={heroImage}
                alt={heroAlt}
                className="w-full object-cover object-center aspect-[21/9]"
              />
            </div>

            <div className="prose-custom">
              {sections.map((section, idx) => (
                <section key={idx} className="mb-12">
                  <h2 className="font-heading text-2xl font-bold text-foreground-900 mb-4">{section.title}</h2>
                  {section.content}
                </section>
              ))}

              {faqs && faqs.length > 0 && (
                <section className="mb-12">
                  <h2 className="font-heading text-2xl font-bold text-foreground-900 mb-4">Domande frequenti</h2>
                  <div className="space-y-4">
                    {faqs.map((faq, idx) => (
                      <details key={idx} className="group bg-background-50 rounded-xl border border-background-200/70">
                        <summary className="flex items-center justify-between p-5 cursor-pointer font-heading text-foreground-900 font-semibold text-base">
                          {faq.q}
                          <i className="ri-arrow-down-s-line text-foreground-400 group-open:rotate-180 transition-transform w-5 h-5 flex items-center justify-center"></i>
                        </summary>
                        <div className="px-5 pb-5 text-foreground-600 text-sm leading-relaxed">
                          {faq.a}
                        </div>
                      </details>
                    ))}
                  </div>
                </section>
              )}

              {conclusion && (
                <section className="mb-12">{conclusion}</section>
              )}
            </div>

            <div className="mt-12 p-6 bg-accent-50/50 rounded-2xl border border-accent-200/50 text-center">
              <h3 className="font-heading text-xl font-bold text-foreground-900 mb-2">Pronto per iniziare?</h3>
              <p className="text-foreground-600 text-sm mb-4">Esplora subito tutte le categorie prodotto e inizia a fare acquisti migliori.</p>
              <div className="flex flex-col sm:flex-row items-center justify-center gap-3">
                <a
                  href="https://kaspreadsheet.com"
                  target="_blank"
                  rel="noopener nofollow"
                  className="px-6 py-3 rounded-full bg-primary-500 text-white text-sm font-semibold hover:bg-primary-600 transition-colors duration-200 whitespace-nowrap cursor-pointer"
                >
                  Itaobuy Sheet
                  <i className="ri-arrow-right-up-line ml-1.5 text-sm"></i>
                </a>
                <Link
                  to="/"
                  className="px-6 py-3 rounded-full border border-background-200 text-foreground-700 text-sm font-medium hover:bg-background-100 transition-colors duration-200 whitespace-nowrap cursor-pointer"
                >
                  Torna alla Home
                </Link>
              </div>
            </div>
          </div>
        </article>
      </main>
      <Footer />
    </>
  );
}